import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import {
  IApiCharacterInfo,
  ICharacterConfig,
  ICharacterSkillSlot,
  ICharacterSpeciality,
  ICharacterTraitSlot,
} from 'src/app/api/interfaces/character.interface';
import {
  ICharacterConfiguration,
  ICharacterSkillConfiguration,
  ICharacterSkillSlotConfigurations,
  ICharacterSlotConfiguration,
  ICharacterSpecialityConfiguration,
  ICharacterTraitConfigurations,
} from 'src/app/api/interfaces/configuration.interface';
import { capitalizeFirstLetter } from 'src/app/modules/main-game/components/game-modals/modal-components/crafting/shared/character-selection/character-selection.component';
import {
  setCharacterState,
  updateCharacterSlot,
  updateCharacterSpecs,
  createCompletedCharacterSlots,
} from './characterState.actions';
export interface ICompletedCharacterSkillSlot {
  skill: {
    skill: ICharacterSkillSlot;
    config: ICharacterSkillConfiguration | null | undefined;
  };
  config: ICharacterSkillSlotConfigurations | undefined;
}
export interface ICharacterCompleted {
  config: ICharacterConfiguration[];
  id: number;
  name: string;
  level: number;
  exp: number;
  skin: ICharacterConfig;
  energy: {
    quantityInPercent: number;
    id: number;
    characterId: number;
    quantity: number;
    capacity: number;
  };
  skillSlots: ICompletedCharacterSkillSlot[];
  traitSlots: {
    trait: ICharacterTraitSlot;
    config: ICharacterTraitConfigurations | undefined;
  }[];
  specialities: {
    displayName: string;
    progress: number;
    speciality: ICharacterSpeciality;
    config: ICharacterSpecialityConfiguration | undefined;
  }[];
  status: 'FREE' | 'BUSY' | 'LOCKED';
  nextLevelProgress: number;
}
export interface ICompletedCharacterSlots {
  id: number;
  character: ICharacterCompleted | null;
  configurationId: string;
  status: 'LOCKED' | 'FREE' | 'BUSY';
  sequenceNumber: number;
  slotConfig: ICharacterSlotConfiguration | undefined;
}
export interface ICharacterStateActions {
  type:
    | '[State API] Get Character state success'
    | 'Update Character state success'
    | string;
}
export interface ICharacterState {
  characterSlots: IApiCharacterInfo[];
  completedCharacterSlots: ICompletedCharacterSlots[];
}
export const initialState: ICharacterState = {
  characterSlots: [],
  completedCharacterSlots: [],
};

const _characterStateReducer = createReducer(
  initialState,
  on(setCharacterState, (state, { responseData }) => {
    return { ...state, ...responseData };
  }),
  on(updateCharacterSlot, (state, { slot }) => {
    return {
      ...state,
      characterSlots: state.characterSlots.map((StateSlot) =>
        StateSlot.id === slot.id ? slot : StateSlot
      ),
    };
  }),
  on(createCompletedCharacterSlots, (state, { configs }) => {
    const completedSlots: ICompletedCharacterSlots[] | any[] =
      state.characterSlots.map((slot) => {
        const slotConfig = configs.characterSlotConfigurations.find(
          (slotConfig) => slotConfig.id === slot.configurationId
        );
        if (slot.character) {
          const characterSkills = slot.character.skillSlots.map(
            (characterSkill) => {
              const skillConfig = characterSkill.characterSkillConfigurationId
                ? configs.characterSkillConfigurations.find(
                    (skillConfig) =>
                      skillConfig.id ===
                      characterSkill.characterSkillConfigurationId
                  )
                : null;
              return {
                skill: { skill: characterSkill, config: skillConfig },
                config: configs.characterSkillSlotConfigurations.find(
                  (skillConfig) =>
                    skillConfig.id === characterSkill.configurationId
                ),
              };
            }
          );
          const characterSpecialities = slot.character.specialities.map(
            (characterSpeciality) => {
              const specialityConfiguration =
                configs.characterSpecialityConfigurations.find(
                  (specialityConfiguration) =>
                    specialityConfiguration.id ===
                    characterSpeciality.configurationId
                );
              return {
                displayName: specialityConfiguration
                  ? capitalizeFirstLetter(
                      specialityConfiguration.speciality.toLocaleLowerCase()
                    )
                  : '',
                progress: specialityConfiguration
                  ? Math.round(
                      (characterSpeciality.exp /
                        specialityConfiguration.levels[
                          characterSpeciality.level + 1
                        ].expTotal) *
                        100
                    )
                  : 0,
                speciality: characterSpeciality,
                config: specialityConfiguration,
              };
            }
          );
          const characterNextLevelProgressConfig =
            configs.characterConfigurations.find(
              (levelConfig) => levelConfig.level === slot.character.level + 1
            );
          const characterNextLevelProggres = characterNextLevelProgressConfig
            ? Math.round(
                (slot.character.exp /
                  characterNextLevelProgressConfig.expTotal) *
                  100
              )
            : 0;
          const characterEnergy = {
            ...slot.character.energy,
            quantityInPercent:
              (slot.character.energy.quantity /
                slot.character.energy.capacity) *
              100,
          };
          const characterTraits = slot.character.traitSlots.map(
            (characterTrait) => {
              return {
                trait: characterTrait,
                config: configs.characterTraitConfigurations.find(
                  (traitConfig) =>
                    traitConfig.id ===
                    characterTrait.characterTraitConfigurationId
                ),
              };
            }
          );

          return {
            ...slot,
            slotConfig: slotConfig,
            character: {
              ...slot.character,
              energy: characterEnergy,
              specialities: characterSpecialities,
              traitSlots: characterTraits,
              skillSlots: characterSkills,
              nextLevelProgress: characterNextLevelProggres,
              config: configs.characterConfigurations,
            },
          };
        } else {
          return { ...slot, slotConfig };
        }
      });
    return { ...state, completedCharacterSlots: completedSlots };
  }),
  on(updateCharacterSpecs, (state, { character }) => {
    const apiArr = state.characterSlots.map((slot) => {
      if (
        slot.character &&
        slot.character !== null &&
        (slot.character.id === character.data.characterId ||
          slot.character.id === character.data.id)
      ) {
        if (character.type === 'character') {
          return {
            ...slot,
            character: {
              ...slot.character,
              exp: character.data.exp,
              level: character.data.level,
              status: character.data.status,
            },
          };
        } else if (character.type === 'energy') {
          return {
            ...slot,
            character: { ...slot.character, energy: character.data },
          };
        } else if (character.type === 'speciality') {
          return {
            ...slot,
            character: {
              ...slot.character,
              specialities: slot.character.specialities.map((spec) => {
                if (spec.id === character.data.id) {
                  return character.data;
                } else {
                  return spec;
                }
              }),
            },
          };
        } else if (character.type === 'skill') {
          const SkillSlot = slot.character.skillSlots.map((skillSlot) => {
            if (skillSlot.id === character.data.id) {
              return { ...skillSlot, ...character.data };
            } else {
              return skillSlot;
            }
          });
          return {
            ...slot,
            character: { ...slot.character, skillSlots: SkillSlot },
          };
        } else {
          return slot;
        }
      } else {
        return slot;
      }
    });
    const arr = state.completedCharacterSlots
      .filter((slot) => slot.character)
      .map((slot) => {
        if (
          slot.character !== null &&
          (slot.character.id === character.data.characterId ||
            slot.character.id === character.data.id)
        ) {
          if (character.type === 'character') {
            return {
              ...slot,
              character: {
                ...slot.character,
                exp: character.data.exp,
                level: character.data.level,
                status: character.data.status,
              },
            };
          } else if (character.type === 'energy') {
            const characterEnergy = {
              ...character.data,
              quantityInPercent:
                (character.data.quantity / character.data.capacity) * 100,
            };
            return {
              ...slot,
              character: { ...slot.character, energy: characterEnergy },
            };
          } else if (character.type === 'speciality') {
            return {
              ...slot,
              character: {
                ...slot.character,
                specialities: slot.character.specialities.map((spec) => {
                  if (spec.speciality.id === character.data.id && spec.config) {
                    const newProgress = Math.round(
                      (character.data.exp /
                        spec.config.levels[character.data.level + 1].expTotal) *
                        100
                    );
                    return {
                      ...spec,
                      speciality: character.data,
                      progress: newProgress,
                    };
                  } else {
                    return spec;
                  }
                }),
              },
            };
          } else if (character.type === 'skill') {
            const SkillSlot = slot.character.skillSlots.map((skillSlot) => {
              if (skillSlot.skill.skill.id === character.data.id) {
                return { ...skillSlot, ...character.data };
              } else {
                return skillSlot;
              }
            });
            return {
              ...slot,
              character: { ...slot.character, skillSlots: SkillSlot },
            };
          } else {
            return slot;
          }
        } else {
          return slot;
        }
      });
    return { ...state, characterSlots: apiArr, completedCharacterSlots: arr };
  })
);

export function characterStateReducer(
  state: ICharacterState | undefined,
  action: ICharacterStateActions
) {
  return _characterStateReducer(state, action);
}

export const selectCharacterState = createSelector(
  createFeatureSelector<ICharacterState>('characterState'),
  (characterState) => characterState
);
